import HmacSHA256 from 'crypto-js/hmac-sha256';
import Hex from 'crypto-js/enc-hex';

const API_BASE_URL = process.env.REACT_APP_DISHLINK_BACKEND_URL || '';
const API_KEY = process.env.REACT_APP_DISHLINK_API_KEY || '';
const CLIENT_ID = process.env.REACT_APP_DISHLINK_CLIENT_ID || '';

let cachedToken = null;
let tokenExpiry = null;

export async function getAuthToken() {
  const now = Date.now();

  if (cachedToken && tokenExpiry && now < tokenExpiry) {
    return cachedToken;
  }

  const loginUrl = `${API_BASE_URL}/login`;
  const response = await fetch(loginUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      apiKey: `${API_KEY}`,
      clientId: `${CLIENT_ID}`,
    }),
  });

  if (!response.ok) {
    throw new Error(`Error fetching token: ${response.statusText}`);
  }

  const data = await response.json();
  cachedToken = data.token;
  tokenExpiry = now + 24 * 60 * 60 * 1000;

  return cachedToken;
}

const makeAuthenticatedAndSignedRequest = async (endpoint, options = {}, authToken) => {
  if (!authToken) {
    await getAuthToken();
  }

  const method = options.method;
  const timestamp = Date.now();
  
  const signingPath = endpoint.split('?')[0];
  const stringToSign = `${method}\n${signingPath}\n${timestamp}`;
  
  const signature = HmacSHA256(stringToSign, API_KEY).toString(Hex);
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      'x-client-id': CLIENT_ID,
      'x-timestamp': timestamp,
      'x-signature': signature,
    },
  });

  if (response.status === 403) {
    await getAuthToken();
    return makeAuthenticatedAndSignedRequest(endpoint, options);
  }

  return response;
};

export async function getDashboardData(userId, token) {
  const url = `/transactions/${encodeURIComponent(userId)}`;
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );
  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function getUserByPhoneNumber(phoneNumber, token) {
  const url = `/customer_by_phone/${encodeURIComponent(phoneNumber)}`;
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );
  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function deleteUserAccount(userId, token) {
  const url = '/delete_user_account';
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { 
      method: 'POST',
      body: JSON.stringify({ user_id: userId }) 
    }, 
    token
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function submitDispute(userId, disputeData, token) {
  const url = '/submit_dispute';
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { 
      method: 'POST',
      body: JSON.stringify({ 
        user_id: userId,
        ...disputeData
      }) 
    }, 
    token
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function checkUserValidity(userId, token) {
  const url = `/validate_user/${encodeURIComponent(userId)}`;
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function getLateFee(locationId, token) {
  const url = `/get_dishlink_configurations/${locationId}`

  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );
  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const configurations = await response.json();
  const lateFee = configurations.late_fee;
  return lateFee;
}

export async function getTransactionsByDate(startDate, endDate, token, userId = null) {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });

  if (userId) {
    params.append('user_id', userId);
  }

  const url = `/get_transactions_by_date?${params.toString()}`;
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function getUsersByDate(startDate, endDate, token) {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });

  const url = `/get_users_by_date?${params.toString()}`;
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function getLateTransactions(startDate, endDate, token) {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });

  const url = `/get_late_fee_transactions?${params.toString()}`;
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
}

export async function refundLateFee(transactionId, numberOfUnits, token) {
  const url = '/refund_late_fee';
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { 
      method: 'POST',
      body: JSON.stringify({ 
        transaction_id: transactionId,
        number_of_units: numberOfUnits 
      }) 
    }, 
    token
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Error: ${response.statusText}`);
  }

  return response.json();
}

export async function searchCustomers(query, token) {
  const params = new URLSearchParams({ query });
  const url = `/search_customers?${params.toString()}`;
  
  const response = await makeAuthenticatedAndSignedRequest(
    url, 
    { method: 'GET' }, 
    token
  );

  if (!response.ok) {
    throw new Error(`Error searching customers: ${response.statusText}`);
  }

  return response.json();
}

export async function markTransactionReturned(transactionId, numberOfUnits, token) {
  const url = '/mark_transaction_returned';

  const response = await makeAuthenticatedAndSignedRequest(
    url,
    {
      method: 'POST',
      body: JSON.stringify({
        transaction_id: transactionId,
        ...(numberOfUnits != null && { number_of_units: numberOfUnits }),
      }),
    },
    token
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Error: ${response.statusText}`);
  }

  return response.json();
}